interface StatsCardProps {
  title: string;
  value: number | string;
  icon: 'users' | 'check-circle' | 'log-out' | 'home' | 'heart' | 'clock' | 'calendar';
  color: 'primary' | 'marigold' | 'success' | 'leaf' | 'warning' | 'danger' | 'coral' | 'sky' | 'gray';
  pulse?: boolean;
  subtitle?: string;
}

const colorMap: Record<string, { bg: string; text: string; accent: string }> = {
  primary:  { bg: 'var(--marigold-soft)',  text: '#7D5507',    accent: 'var(--marigold)' },
  marigold: { bg: 'var(--marigold-soft)',  text: '#7D5507',    accent: 'var(--marigold)' },
  success:  { bg: 'var(--leaf-soft)',      text: '#2F7A4E',    accent: 'var(--leaf)' },
  leaf:     { bg: 'var(--leaf-soft)',      text: '#2F7A4E',    accent: 'var(--leaf)' },
  warning:  { bg: 'var(--card-tint-cream)', text: '#A97209',   accent: 'var(--marigold)' },
  danger:   { bg: 'var(--coral-soft)',     text: 'var(--coral)', accent: 'var(--coral)' },
  coral:    { bg: 'var(--coral-soft)',     text: 'var(--coral)', accent: 'var(--coral)' },
  sky:      { bg: 'var(--sky-soft)',       text: '#4E8EA8',    accent: 'var(--sky)' },
  gray:     { bg: 'rgba(25,38,49,0.06)',   text: 'var(--ink-soft)', accent: 'var(--ink-faint)' },
};

const iconPaths: Record<string, string> = {
  users:         'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z',
  'check-circle':'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  'log-out':     'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1',
  home:          'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
  heart:         'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z',
  clock:         'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  calendar:      'M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
};

export function StatsCard({ title, value, icon, color, pulse = false, subtitle }: StatsCardProps) {
  const c = colorMap[color] ?? colorMap.gray;
  const numVal = typeof value === 'number' ? value.toLocaleString() : value;

  return (
    <div
      style={{
        background: 'var(--paper)',
        border: '1px solid var(--border)',
        borderRadius: '14px 14px 48px 14px',
        boxShadow: 'var(--shadow-card)',
        padding: '20px 20px 24px',
        position: 'relative',
        overflow: 'visible',
        transition: 'box-shadow 200ms var(--ease-out), transform 200ms var(--ease-out)',
        cursor: 'default',
      }}
      onMouseEnter={e => {
        (e.currentTarget as HTMLElement).style.boxShadow = 'var(--shadow-soft)';
        (e.currentTarget as HTMLElement).style.transform = 'translateY(-2px)';
      }}
      onMouseLeave={e => {
        (e.currentTarget as HTMLElement).style.boxShadow = 'var(--shadow-card)';
        (e.currentTarget as HTMLElement).style.transform = 'translateY(0)';
      }}
    >
      {/* Tape accent on focus card */}
      {pulse && (
        <div style={{
          position: 'absolute', left: 16, top: -8,
          width: 44, height: 9,
          background: c.accent,
          borderRadius: 2,
          transform: 'rotate(-2.5deg)',
          opacity: 0.9,
        }} />
      )}

      {/* Icon medallion */}
      <div style={{
        width: 40, height: 40,
        background: c.bg,
        borderRadius: 12,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        marginBottom: 14,
        border: `1.5px solid ${c.accent}22`,
      }}>
        <svg width="20" height="20" fill="none" stroke={c.accent} strokeWidth="1.75" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d={iconPaths[icon]} />
        </svg>
      </div>

      {/* Value */}
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
        <span style={{
          fontFamily: 'var(--font-display)',
          fontSize: 30, fontWeight: 700,
          letterSpacing: '-0.04em',
          color: 'var(--ink)',
          lineHeight: 1,
        }}>
          {numVal}
        </span>
        {pulse && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            <span
              className="signal-dot signal-dot--live"
              style={{ width: 7, height: 7 }}
            />
            <span style={{
              fontSize: 9, fontWeight: 700, letterSpacing: '0.12em',
              textTransform: 'uppercase', color: '#2F7A4E',
            }}>
              Live
            </span>
          </div>
        )}
      </div>

      {/* Label */}
      <p style={{
        fontFamily: 'var(--font-body)',
        fontSize: 11, fontWeight: 700,
        letterSpacing: '0.1em', textTransform: 'uppercase',
        color: 'var(--ink-faint)',
        margin: 0,
      }}>
        {title}
      </p>

      {subtitle && (
        <p style={{
          fontFamily: 'var(--font-body)',
          fontSize: 11, color: 'var(--ink-faint)',
          margin: '4px 0 0',
        }}>
          {subtitle}
        </p>
      )}
    </div>
  );
}